import { Building04, ChevronDown, DotsGrid } from "@untitledui/icons";
import { motion } from "motion/react";
import {
	type ReactElement,
	type ReactNode,
	useCallback,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import { BadgeWithIcon } from "@/components/base/badges/badges";
import { cx } from "@/utils/cx";

export const AccordionItemSize = {
	Sm: "sm",
	Md: "md",
	Lg: "lg",
} as const;

export type AccordionItemSize =
	(typeof AccordionItemSize)[keyof typeof AccordionItemSize];

const titleSizeClass: Record<AccordionItemSize, string> = {
	sm: "text-sm font-bold leading-5",
	md: "text-md font-bold leading-6",
	lg: "text-lg font-bold leading-7",
};

export interface AccordionItemProps {
	/** Bold header title. */
	primaryText: ReactNode;
	/** Supporting line under the title (e.g. author • date). */
	secondaryText?: ReactNode;
	/**
	 * Whether to show secondary text.
	 * @default true
	 */
	showSecondaryText?: boolean;
	/**
	 * Whether to show the trailing badge.
	 * @default true
	 */
	showBadge?: boolean;
	/** Badge label when `showBadge` is true. @default "Badge" */
	badgeLabel?: string;
	/** Custom badge node; overrides `badgeLabel` when provided. */
	badge?: ReactNode;
	/**
	 * Whether to show the leading slot (drag handle by default).
	 * @default false
	 */
	showLeadingSlot?: boolean;
	/** Custom leading slot content. Defaults to a dots-grid icon. */
	leadingSlot?: ReactNode;
	/**
	 * Whether to draw the card border.
	 * @default true
	 */
	showBorder?: boolean;
	/**
	 * Title typography size.
	 * @default "md"
	 */
	size?: AccordionItemSize;
	/**
	 * When true, primary title is a single line with ellipsis overflow.
	 * @default false
	 */
	truncatePrimaryText?: boolean;
	/** Controlled open state. */
	isExpanded?: boolean;
	/**
	 * Initial open state when uncontrolled.
	 * @default false
	 */
	defaultExpanded?: boolean;
	/** Fires when the user toggles expansion. */
	onExpandedChange?: (expanded: boolean) => void;
	/** Panel body shown when expanded. */
	children?: ReactNode;
	className?: string;
	/** Optional test id override. */
	"data-testid"?: string;
}

/**
 * Expandable panel with a header (title, optional secondary text / badge /
 * leading slot) and a collapsible content area.
 *
 * @see Figma AccordionItem 3521:15307
 */
export function AccordionItem({
	primaryText,
	secondaryText,
	showSecondaryText = true,
	showBadge = true,
	badgeLabel = "Badge",
	badge,
	showLeadingSlot = false,
	leadingSlot,
	showBorder = true,
	size = AccordionItemSize.Md,
	truncatePrimaryText = false,
	isExpanded: controlledExpanded,
	defaultExpanded = false,
	onExpandedChange,
	children,
	className,
	"data-testid": dataTestId = "accordion-item",
}: AccordionItemProps): ReactElement {
	const reactId = useId();
	const panelId = `accordion-item-panel-${reactId}`;
	const headingId = `accordion-item-heading-${reactId}`;
	const triggerId = `accordion-item-trigger-${reactId}`;

	const [internalOpen, setInternalOpen] = useState(defaultExpanded);
	const isControlled = controlledExpanded !== undefined;
	const open = isControlled ? controlledExpanded : internalOpen;
	const panelBodyRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (open) return;
		const root = panelBodyRef.current;
		if (!root) return;
		const active = document.activeElement;
		if (active instanceof Node && root.contains(active)) {
			document.getElementById(triggerId)?.focus();
		}
	}, [open, triggerId]);

	const toggle = useCallback(() => {
		const next = !open;
		if (!isControlled) {
			setInternalOpen(next);
		}
		onExpandedChange?.(next);
	}, [open, isControlled, onExpandedChange]);

	const headerPadding = showBorder
		? "px-5 py-3"
		: open
			? "px-4 py-3"
			: "px-0 py-3";

	return (
		<div
			className={cx(
				"flex flex-col items-start overflow-clip rounded-xl",
				showBorder && "border border-primary",
				// Open: grey surface; collapsed: white (Figma AccordionItem State=Open).
				showBorder && (open ? "bg-secondary" : "bg-primary"),
				!showBorder && open && "bg-tertiary",
				showBorder && !open && "hover:bg-primary_hover",
				"has-[:focus-visible]:shadow-[0_0_0_2px_var(--color-bg-primary),0_0_0_4px_var(--color-focus-ring)]",
				className,
			)}
			data-testid={dataTestId}
			data-expanded={open || undefined}
		>
			<button
				type="button"
				id={triggerId}
				aria-expanded={open}
				aria-controls={panelId}
				onClick={toggle}
				className={cx(
					"flex w-full cursor-pointer items-center gap-4 text-left outline-none",
					headerPadding,
					open && showBorder && "border-b border-primary bg-secondary",
					open && !showBorder && "bg-tertiary",
				)}
			>
				{showLeadingSlot && (
					<span
						aria-hidden
						className="flex shrink-0 items-center self-stretch text-fg-quaternary"
					>
						{leadingSlot ?? <DotsGrid className="size-5" />}
					</span>
				)}

				<span
					className={cx(
						"flex min-h-[38px] min-w-0 flex-1 flex-col items-start justify-center",
						!truncatePrimaryText && "break-words",
					)}
				>
					<span
						id={headingId}
						className={cx(
							"w-full text-primary",
							titleSizeClass[size],
							truncatePrimaryText && "truncate",
						)}
					>
						{primaryText}
					</span>
					{showSecondaryText &&
						secondaryText != null &&
						secondaryText !== "" && (
							<span className="w-full text-xs font-medium leading-[18px] text-secondary">
								{secondaryText}
							</span>
						)}
				</span>

				{showBadge &&
					(badge ?? (
						<BadgeWithIcon color="gray" size="sm" iconLeading={Building04}>
							{badgeLabel}
						</BadgeWithIcon>
					))}

				{/* Decorative chevron — header button owns the interaction (avoids nested buttons). */}
				<span
					aria-hidden
					className="flex size-9 shrink-0 items-center justify-center rounded-full text-fg-quaternary"
				>
					<ChevronDown
						className={cx(
							"size-5 transition-transform duration-200 ease-out",
							open && "rotate-180",
						)}
					/>
				</span>
			</button>

			<motion.div
				id={panelId}
				role="region"
				aria-labelledby={headingId}
				className="w-full overflow-hidden"
				initial={false}
				animate={{
					height: open ? "auto" : 0,
					opacity: open ? 1 : 0,
				}}
				transition={{
					type: "spring",
					damping: 24,
					stiffness: 240,
					bounce: 0.2,
				}}
			>
				<div
					ref={panelBodyRef}
					className={cx(
						"w-full p-4",
						showBorder ? "bg-secondary" : "bg-tertiary",
						!open && "pointer-events-none select-none",
					)}
					aria-hidden={!open}
					inert={!open || undefined}
				>
					{children}
				</div>
			</motion.div>
		</div>
	);
}
